import { type ReactNode, useCallback, useEffect, useMemo, useRef } from "react";
import { useAuth as useOidcAuth } from "react-oidc-context";
import { AuthContext } from "./AuthProvider";
import type { AuthContextValue } from "./types";

interface AuthProviderInnerProps {
  children: ReactNode;
}

/**
 * Inner provider that manages OIDC state and provides the auth context.
 * Must be wrapped in an OidcAuthProvider.
 */
export function AuthProviderInner({ children }: AuthProviderInnerProps) {
  const oidc = useOidcAuth();
  const hasAttemptedSilentSignin = useRef(false);

  // Attempt silent sign-in once when component mounts
  useEffect(() => {
    if (hasAttemptedSilentSignin.current) {
      return;
    }

    // Never attempt silent sign-in from inside an iframe (e.g. silent-renew
    // frame or an IDP-redirected login page served by the SPA fallback).
    // Without this guard the iframe-loaded SPA would spawn another
    // signinSilent() iframe, cascading into an infinite reload loop.
    if (window.self !== window.top) {
      return;
    }

    if (oidc.isLoading || oidc.isAuthenticated || oidc.user) {
      return;
    }

    if (oidc.activeNavigator) {
      return;
    }

    if (
      window.location.pathname.endsWith("/auth/callback") ||
      window.location.pathname.endsWith("/auth/silent-renew") ||
      window.location.pathname.endsWith("/auth/login")
    ) {
      return;
    }

    hasAttemptedSilentSignin.current = true;
    void oidc.signinSilent().catch(() => {
      // login_required and similar errors are expected when there is no active IDP session
    });
  }, [
    oidc.isLoading,
    oidc.isAuthenticated,
    oidc.user,
    oidc.activeNavigator,
    oidc.signinSilent,
  ]);

  const getAccessToken = useCallback(() => {
    return oidc.user?.access_token;
  }, [oidc.user]);

  const login = useCallback(
    async (returnUrl?: string) => {
      const state = returnUrl ? { returnUrl } : undefined;
      await oidc.signinRedirect({ state });
    },
    [oidc.signinRedirect],
  );

  const logout = useCallback(async () => {
    await oidc.signoutRedirect();
  }, [oidc.signoutRedirect]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: oidc.user ?? null,
      isLoading: oidc.isLoading,
      isAuthenticated: oidc.isAuthenticated,
      getAccessToken,
      login,
      logout,
      error: oidc.error ?? null,
    }),
    [
      oidc.user,
      oidc.isLoading,
      oidc.isAuthenticated,
      oidc.error,
      getAccessToken,
      login,
      logout,
    ],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
